import React from 'react';
import { Controller } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputTextarea } from 'primereact/inputtextarea';
import TextSnippetWrapper from 'components/TextSnippetWrapper';

/**
 * Insurance Provider Fields Component
 *
 * This component renders additional fields when a business contact is assigned
 * the "Insurance Provider" role. It includes:
 * - Insurance type (Client/Defendant)
 * - Conditional toggle for PIP Exhausted (Client) or Policy Limit Search (Defendant)
 * - Policy number
 * - Claim number
 * - Policy limit
 * - Notes
 */
export default function InsuranceProviderFields({ control, watch, setValue, getFormErrorMessage }) {
  const insuranceType = watch('insurance_type');

  const insuranceTypeOptions = [
    { label: 'Client Insurance', value: 'client' },
    { label: 'Defendant Insurance', value: 'defendant' },
  ];

  return (
    <div className="insurance-provider-fields">
      <div className="row mb-3">
        <div className="col-12">
          <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            <h6 className="mb-3">Insurance Provider Information</h6>

            {/* Insurance Type */}
            <div className="row mb-3">
              <div className="col-12 col-lg-4 mt-2">
                <label className="p-medium">
                  Insurance For<span className="text-danger"> *</span>
                </label>
              </div>
              <div className="col-12 col-lg-8">
                <Controller
                  name="insurance_type"
                  control={control}
                  rules={{ required: 'Insurance type is required' }}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={insuranceTypeOptions}
                      placeholder="Select"
                      className="w-100"
                      onChange={(e) => {
                        field.onChange(e.value);
                        // Reset conditional fields when type changes
                        if (e.value === 'client') {
                          setValue('policy_limit_search_conducted', false);
                        } else {
                          setValue('pip_exhausted', false);
                        }
                      }}
                    />
                  )}
                />
                {getFormErrorMessage && getFormErrorMessage('insurance_type')}
              </div>
            </div>

            {/* Conditional Toggle - PIP Exhausted (Client) or Policy Limit Search (Defendant) */}
            {insuranceType && (
              <div className="row mb-3">
                <div className="col-12 col-lg-4 mt-2">
                  <label className="p-medium">{insuranceType === 'client' ? 'PIP Exhausted' : 'Policy Limit Search Conducted'}</label>
                </div>
                <div className="col-12 col-lg-8">
                  <Controller
                    name={insuranceType === 'client' ? 'pip_exhausted' : 'policy_limit_search_conducted'}
                    control={control}
                    render={({ field }) => (
                      <InputSwitch
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.value)}
                        className="border-0 input-swich"
                      />
                    )}
                  />
                  {insuranceType === 'client' && (
                    <small className="d-block mt-1 text-muted">Toggle on if Personal Injury Protection (PIP) has been exhausted</small>
                  )}
                  {insuranceType === 'defendant' && (
                    <small className="d-block mt-1 text-muted">Toggle on if policy limit search has been conducted</small>
                  )}
                </div>
              </div>
            )}

            {/* Policy Number */}
            <div className="row mb-3">
              <div className="col-12 col-lg-4 mt-2">
                <label className="p-medium">Policy Number</label>
              </div>
              <div className="col-12 col-lg-8">
                <Controller
                  name="policy_number"
                  control={control}
                  render={({ field }) => (
                    <TextSnippetWrapper {...field} type="text" className="input-shadow w-100" placeholder="Enter policy number" />
                  )}
                />
              </div>
            </div>

            {/* Claim Number */}
            <div className="row mb-3">
              <div className="col-12 col-lg-4 mt-2">
                <label className="p-medium">Claim Number</label>
              </div>
              <div className="col-12 col-lg-8">
                <Controller
                  name="claim_number"
                  control={control}
                  render={({ field }) => (
                    <TextSnippetWrapper {...field} type="text" className="input-shadow w-100" placeholder="Enter claim number" />
                  )}
                />
              </div>
            </div>

            {/* Policy Limit */}
            <div className="row mb-3">
              <div className="col-12 col-lg-4 mt-2">
                <label className="p-medium">Policy Limit</label>
              </div>
              <div className="col-12 col-lg-8">
                <Controller
                  name="policy_limit"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      keyfilter="pint"
                      className="input-shadow w-100"
                      placeholder="Enter policy limit"
                      onChange={(e) => field.onChange(e.target.value)}
                      onKeyDown={(e) => {
                        // Prevent 'e', 'E', '+', '-' which are allowed in type="number"
                        if (['e', 'E', '+', '-'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  )}
                />
                <small className="d-block mt-1 text-muted">Enter as a numeric value (e.g., 50000 for $50,000)</small>
              </div>
            </div>

            {/* Notes */}
            <div className="row mb-3">
              <div className="col-12 col-lg-4 mt-2">
                <label className="p-medium">Notes</label>
              </div>
              <div className="col-12 col-lg-8">
                <Controller
                  name="insurance_notes"
                  control={control}
                  render={({ field }) => (
                    <InputTextarea
                      {...field}
                      rows={3}
                      className="input-shadow w-100"
                      placeholder="Enter any additional notes about this insurance provider"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
